import React, { useState, useEffect } from 'react';
import { Search, FileText, MapPin, Stethoscope, Save, Plus, Trash2, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { seoAPI, pagesAPI, listingsAPI, citiesAPI, specialtiesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const SEOManager = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('listings');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [listings, setListings] = useState([]);
  const [cities, setCities] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [staticPages, setStaticPages] = useState([]);
  
  // Selected item for editing
  const [selectedItem, setSelectedItem] = useState(null);
  const [seoData, setSeoData] = useState({
    meta_title: '',
    meta_description: '',
    focus_keywords: [],
    excerpt: '',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
    no_index: false,
    schema_type: '',
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  
  // New page dialog
  const [showNewPageDialog, setShowNewPageDialog] = useState(false);
  const [newPage, setNewPage] = useState({ slug: '', title: '', content: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listingsRes, citiesRes, specialtiesRes, pagesRes] = await Promise.all([
        listingsAPI.getAll({ limit: 100 }),
        citiesAPI.getAll(),
        specialtiesAPI.getAll(),
        pagesAPI.getAll(),
      ]);
      
      setListings(listingsRes.data);
      setCities(citiesRes.data);
      setSpecialties(specialtiesRes.data);
      setStaticPages(pagesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSEOSettings = async (pageType, pageId) => {
    try {
      const res = await seoAPI.get(pageType, pageId);
      const seo = res.data.seo || {};
      setSeoData({
        meta_title: seo.meta_title || '',
        meta_description: seo.meta_description || '',
        focus_keywords: seo.focus_keywords || [],
        excerpt: seo.excerpt || '',
        og_title: seo.og_title || '',
        og_description: seo.og_description || '',
        og_image: seo.og_image || '',
        canonical_url: seo.canonical_url || '',
        no_index: seo.no_index || false,
        schema_type: seo.schema_type || '',
      });
    } catch (error) {
      console.error('Error loading SEO settings:', error);
    }
  };

  const handleSelectItem = async (item, type) => {
    const pageId = type === 'listing' ? item.listing_id : item.slug;
    setSelectedItem({ ...item, type, pageId });
    await loadSEOSettings(type, pageId);
  };

  const handleSaveSEO = async () => {
    if (!selectedItem) return;
    
    setSaving(true);
    try {
      await seoAPI.update(selectedItem.type, selectedItem.pageId, seoData);
      toast.success('SEO settings saved!');
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !seoData.focus_keywords.includes(keywordInput.trim())) {
      setSeoData({
        ...seoData,
        focus_keywords: [...seoData.focus_keywords, keywordInput.trim()],
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setSeoData({
      ...seoData,
      focus_keywords: seoData.focus_keywords.filter((k) => k !== keyword),
    });
  };

  const handleCreatePage = async () => {
    if (!newPage.slug || !newPage.title) {
      toast.error('Slug and title are required');
      return;
    }
    
    try {
      await pagesAPI.create(newPage);
      toast.success('Page created!');
      setShowNewPageDialog(false);
      setNewPage({ slug: '', title: '', content: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      await pagesAPI.delete(slug);
      toast.success('Page deleted');
      if (selectedItem?.slug === slug) {
        setSelectedItem(null);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Admin access required for SEO management.</p>
        </div>
      </div>
    );
  }

  const renderItemList = (items, type, nameField = 'name') => (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {items.map((item) => {
        const id = type === 'listing' ? item.listing_id : item.slug;
        const isSelected = selectedItem?.pageId === id;
        
        return (
          <div
            key={id}
            onClick={() => handleSelectItem(item, type)}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{item[nameField] || item.title}</p>
                {type === 'listing' && (
                  <p className="text-sm text-gray-500">{item.city}, {item.state}</p>
                )}
                {type === 'static' && (
                  <p className="text-sm text-gray-500">/{item.slug}</p>
                )}
              </div>
              {type === 'listing' && (
                <Badge variant="outline" className="text-xs">{item.listing_type}</Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="seo-manager">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Search className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">SEO Manager</h1>
              <p className="text-gray-600 text-sm">Optimize pages for search engines</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Item Selection */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Page</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full grid grid-cols-4">
                    <TabsTrigger value="listings" className="text-xs">Listings</TabsTrigger>
                    <TabsTrigger value="cities" className="text-xs">Cities</TabsTrigger>
                    <TabsTrigger value="specialties" className="text-xs">Specialties</TabsTrigger>
                    <TabsTrigger value="static" className="text-xs">Pages</TabsTrigger>
                  </TabsList>

                  <TabsContent value="listings" className="mt-4">
                    {loading ? (
                      <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      renderItemList(listings, 'listing')
                    )}
                  </TabsContent>

                  <TabsContent value="cities" className="mt-4">
                    {renderItemList(cities, 'city')}
                  </TabsContent>

                  <TabsContent value="specialties" className="mt-4">
                    {renderItemList(specialties, 'specialty')}
                  </TabsContent>

                  <TabsContent value="static" className="mt-4">
                    <div className="mb-4">
                      <Dialog open={showNewPageDialog} onOpenChange={setShowNewPageDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Create New Page
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create Static Page</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div>
                              <Label>Slug (URL path)</Label>
                              <Input
                                placeholder="about-us"
                                value={newPage.slug}
                                onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                              />
                              <p className="text-xs text-gray-500 mt-1">URL: /pages/{newPage.slug || 'your-slug'}</p>
                            </div>
                            <div>
                              <Label>Title</Label>
                              <Input
                                placeholder="About Us"
                                value={newPage.title}
                                onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                              />
                            </div>
                            <div>
                              <Label>Content (optional)</Label>
                              <Textarea
                                placeholder="Page content..."
                                value={newPage.content}
                                onChange={(e) => setNewPage({ ...newPage, content: e.target.value })}
                                rows={4}
                              />
                            </div>
                            <Button onClick={handleCreatePage} className="w-full">Create Page</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {staticPages.length > 0 ? (
                      <div className="space-y-2">
                        {staticPages.map((page) => (
                          <div
                            key={page.slug}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedItem?.pageId === page.slug ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div onClick={() => handleSelectItem(page, 'static')}>
                                <p className="font-medium text-gray-900">{page.title}</p>
                                <p className="text-sm text-gray-500">/{page.slug}</p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePage(page.slug);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-8">No static pages yet</p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - SEO Editor */}
          <div className="lg:col-span-2">
            {selectedItem ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {selectedItem.name || selectedItem.title}
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        Type: {selectedItem.type} | ID: {selectedItem.pageId}
                      </p>
                    </div>
                    <Button onClick={handleSaveSEO} disabled={saving} data-testid="save-seo-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save SEO'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Meta Title */}
                  <div>
                    <Label>Meta Title</Label>
                    <Input
                      placeholder="Page title for search engines (50-60 characters)"
                      value={seoData.meta_title}
                      onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
                      maxLength={70}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {seoData.meta_title.length}/60 characters
                      {seoData.meta_title.length > 60 && <span className="text-amber-600"> (may be truncated)</span>}
                    </p>
                  </div>

                  {/* Meta Description */}
                  <div>
                    <Label>Meta Description</Label>
                    <Textarea
                      placeholder="Brief description for search results (150-160 characters)"
                      value={seoData.meta_description}
                      onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
                      rows={3}
                      maxLength={200}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {seoData.meta_description.length}/160 characters
                      {seoData.meta_description.length > 160 && <span className="text-amber-600"> (may be truncated)</span>}
                    </p>
                  </div>

                  {/* Focus Keywords */}
                  <div>
                    <Label>Focus Keywords</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Add keyword"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                      />
                      <Button type="button" onClick={addKeyword} variant="outline">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {seoData.focus_keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {seoData.focus_keywords.map((keyword) => (
                          <Badge key={keyword} variant="secondary" className="pl-3">
                            {keyword}
                            <button onClick={() => removeKeyword(keyword)} className="ml-2 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Excerpt */}
                  <div>
                    <Label>Excerpt / Summary</Label>
                    <Textarea
                      placeholder="Short summary for previews and social sharing"
                      value={seoData.excerpt}
                      onChange={(e) => setSeoData({ ...seoData, excerpt: e.target.value })}
                      rows={2}
                    />
                  </div>

                  {/* Open Graph Settings */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Open Graph (Social Sharing)</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>OG Title</Label>
                        <Input
                          placeholder="Title for social media shares"
                          value={seoData.og_title}
                          onChange={(e) => setSeoData({ ...seoData, og_title: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label>OG Image URL</Label>
                        <Input
                          placeholder="https://example.com/image.jpg"
                          value={seoData.og_image}
                          onChange={(e) => setSeoData({ ...seoData, og_image: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label>OG Description</Label>
                      <Textarea
                        placeholder="Description for social media shares"
                        value={seoData.og_description}
                        onChange={(e) => setSeoData({ ...seoData, og_description: e.target.value })}
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Advanced Settings */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Advanced Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Canonical URL</Label>
                        <Input
                          placeholder="https://medsearcher.com/page"
                          value={seoData.canonical_url}
                          onChange={(e) => setSeoData({ ...seoData, canonical_url: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label>Schema Type</Label>
                        <Select
                          value={seoData.schema_type}
                          onValueChange={(v) => setSeoData({ ...seoData, schema_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select schema type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MedicalClinic">MedicalClinic</SelectItem>
                            <SelectItem value="Hospital">Hospital</SelectItem>
                            <SelectItem value="Physician">Physician</SelectItem>
                            <SelectItem value="Pharmacy">Pharmacy</SelectItem>
                            <SelectItem value="LocalBusiness">LocalBusiness</SelectItem>
                            <SelectItem value="WebPage">WebPage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <Switch
                        checked={seoData.no_index}
                        onCheckedChange={(checked) => setSeoData({ ...seoData, no_index: checked })}
                      />
                      <Label className="cursor-pointer">
                        No Index (hide from search engines)
                      </Label>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Search Preview</h3>
                    <div className="bg-white border rounded-lg p-4">
                      <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                        {seoData.meta_title || 'Page Title'}
                      </p>
                      <p className="text-green-700 text-sm">
                        medsearcher.com/{selectedItem.type}/{selectedItem.pageId}
                      </p>
                      <p className="text-gray-600 text-sm mt-1">
                        {seoData.meta_description || 'Add a meta description to preview how this page will appear in search results.'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
                    Select a page to edit
                  </h3>
                  <p className="text-gray-600">
                    Choose a listing, city, specialty, or static page from the left panel to edit its SEO settings.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOManager;
